import { useState } from "react";
import { Button, CloseButton, Dialog, Portal, Text, Box, VStack, HStack } from "@chakra-ui/react";
import { FiUpload, FiFile, FiX } from "react-icons/fi";
import { KYC_KEYS } from "../../Utils/Constants";

export const KYCDocumentUploadModal = ({ isOpen, onClose, onSubmit, userId }) => {
  const [files, setFiles] = useState({
    front: null,
    back: null,
    other: null
  });
  const [filePreviews, setFilePreviews] = useState({
    front: null,
    back: null,
    other: null
  });

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image (JPEG/PNG) or PDF file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB');
        return;
      }

      setFiles(prev => ({
        ...prev,
        [type]: file
      }));

      // Create preview for image files
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews(prev => ({
            ...prev,
            [type]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreviews(prev => ({
          ...prev,
          [type]: null
        }));
      }
    }
  };

  const removeFile = (type) => {
    setFiles(prev => ({
      ...prev,
      [type]: null
    }));
    setFilePreviews(prev => ({
      ...prev,
      [type]: null
    }));
  };

  const handleSubmit = () => {
    // Check if at least one file is uploaded
    const hasFiles = Object.values(files).some(file => file !== null);
    if (!hasFiles) {
      alert('Please upload at least one document');
      return;
    }
    
    console.log('KYC modal handleSubmit called, sending data:', { userId, files }); // Debug log

    onSubmit({
      userId,
      files
    });
  };

  const getFileName = (file) => {
    if (!file) return '';
    return file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name;
  };

  return (
    <Dialog.Root open={isOpen} size="lg" placement="center" motionPreset="slide-in-bottom">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="600px"
            maxH="80vh"
            p={4}
            borderRadius="md"
            bg="white"
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header display="flex" justifyContent="space-between" alignItems="center">
              <Dialog.Title>
                Upload KYC Documents
              </Dialog.Title>
              <CloseButton size="sm" onClick={onClose} />
            </Dialog.Header>
            
            {/* User Information Section */}
            {userId && (
              <Box 
                bg="gray.50" 
                p={3} 
                borderRadius="md" 
                mb={4}
                border="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Uploading documents for:
                </Text>
                <Text fontSize="md" color="gray.700">
                  {userId.fname} {userId.lname} ({userId.email})
                </Text>
              </Box>
            )}

            <Dialog.Body
              pt={4}
              flexGrow={1}
              display="flex"
              flexDirection="column"
              gap={4}
            >
              <VStack spacing={4} align="stretch">
                {/* Front Document Upload */}
                <Box>
                  <Text fontWeight="medium" mb={2}>{KYC_KEYS.front}</Text>
                  <HStack spacing={3}>
                    <label htmlFor="front-upload" style={{ width: '100%' }}>
                      <Box
                        border="2px dashed #cbd5e0"
                        borderRadius="md"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ borderColor: "#a0aec0" }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                      >
                        <FiUpload size="24px" color="#718096" />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          {files.front ? getFileName(files.front) : "Click to upload front document"}
                        </Text>
                      </Box>
                      <input
                        id="front-upload"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange('front', e)}
                      />
                    </label>
                    {files.front && (
                      <Button
                        variant="ghost"
                        size="sm"
                        colorPalette="red"
                        onClick={() => removeFile('front')}
                      >
                        <FiX />
                      </Button>
                    )}
                  </HStack>
                </Box>

                {/* Back Document Upload */}
                <Box>
                  <Text fontWeight="medium" mb={2}>{KYC_KEYS.back}</Text>
                  <HStack spacing={3}>
                    <label htmlFor="back-upload" style={{ width: '100%' }}>
                      <Box
                        border="2px dashed #cbd5e0"
                        borderRadius="md"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ borderColor: "#a0aec0" }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                      >
                        <FiUpload size="24px" color="#718096" />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          {files.back ? getFileName(files.back) : "Click to upload back document"}
                        </Text>
                      </Box>
                      <input
                        id="back-upload"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange('back', e)}
                      />
                    </label>
                    {files.back && (
                      <Button
                        variant="ghost"
                        size="sm"
                        colorPalette="red"
                        onClick={() => removeFile('back')}
                      >
                        <FiX />
                      </Button>
                    )}
                  </HStack>
                </Box>

                {/* Other Document Upload */}
                <Box>
                  <Text fontWeight="medium" mb={2}>{KYC_KEYS.other}</Text>
                  <HStack spacing={3}>
                    <label htmlFor="other-upload" style={{ width: '100%' }}>
                      <Box
                        border="2px dashed #cbd5e0"
                        borderRadius="md"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ borderColor: "#a0aec0" }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                      >
                        <FiUpload size="24px" color="#718096" />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          {files.other ? getFileName(files.other) : "Click to upload other document"}
                        </Text>
                      </Box>
                      <input
                        id="other-upload"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange('other', e)}
                      />
                    </label>
                    {files.other && (
                      <Button
                        variant="ghost"
                        size="sm"
                        colorPalette="red"
                        onClick={() => removeFile('other')}
                      >
                        <FiX />
                      </Button>
                    )}
                  </HStack>
                </Box>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer mt={4} justifyContent="flex-end" gap={3}>
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                colorPalette="blue"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};